import React, { useEffect, useRef, useState } from 'react';
import { Icon } from './icons';
import { MAX_LEVEL } from '../todoTree';
import { dueState } from '../todoViews';
import { dueLabel } from '../../lib/quickAdd';

/*
 * The board card's own popup lived here (Gordon, item 17). Since 26 Oct a
 * card opens the side panel (TodoPanel.js) like every other to-do, and what
 * is left is the sub-task tree both drew. The popup's original brief, kept
 * for the record:
 *
 * A BOARD CARD, OPENED. Gordon: "In the board view - when I click on an
 * item/board it does not expand to its parent and children - needs to be
 * fixed. If not in this manner then through a popup showing the sub & sub
 * sub tasks."
 *
 * So a card opens into this: Trello's card popup with Todoist's sub-tasks
 * in it. The to-do's title (click to type over it), its ring, its date,
 * where it is (list › section), whose it is and its labels; then its
 * SUB-TASKS as the tree the list shows - two levels under the card,
 * indented, each tickable, a parent foldable, "+ Add sub-task" at every
 * level that may still have one - a sub-task is never a card of its own,
 * so this is the only place on the board they can be seen and ticked; then
 * the to-do's comments and activity (TodoDetail), and "Edit" for every
 * other field. A dialog on a desk, a sheet along the foot of a phone
 * (TodoPeople's Dialog does both).
 *
 * A card is always a root - the board never shows a sub-task as a card -
 * but should one arrive here anyway, its parent is a breadcrumb at the top
 * that opens the parent instead.
 *
 * `kidsOf(id)` are the open sub-tasks of a to-do, in order; `doneKidsOf(id)`
 * the ticked ones, shown ticked at the end so "2/5" can be seen to add up.
 * `startAt: 'subtasks'` ("2/5 ▸" on the card) scrolls to the sub-tasks.
 * The page does every call; `on` says what was asked for.
 */
/**
 * A TO-DO'S SUB-TASKS as the list shows them: two levels under it,
 * indented, each tickable, a parent foldable, "+ Add sub-task" at every
 * level that may still have one, the ticked ones at the end of their level.
 * The side panel (TodoPanel.js) draws it.
 * `level` is where `item` itself sits (0 a root).
 */
export function SubtaskTree({ item, level = 0, now, todayIso, busy, completing, kidsOf, doneKidsOf, on, startAt = null }) {
  const [folded, setFolded] = useState(() => new Set());
  const [adding, setAdding] = useState(null);     // id of the to-do getting a sub-task
  const [text, setText] = useState('');
  const subsRef = useRef(null);

  // "2/5 ▸" on the card: straight to the sub-tasks.
  useEffect(() => {
    if (startAt !== 'subtasks' || !subsRef.current) return;
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    subsRef.current.scrollIntoView({ block: 'start', behavior: reduce ? 'auto' : 'smooth' });
  }, [startAt]);

  const ticking = (t) => completing.has(t.id);
  const countUnder = (id) => {
    const open = kidsOf(id);
    const done = doneKidsOf(id);
    return { open: open.length, done: done.length, total: open.length + done.length };
  };
  const totals = countUnder(item.id);

  const fold = (id, on_) => setFolded((prev) => {
    const next = new Set(prev);
    if (on_) next.add(id); else next.delete(id);
    return next;
  });

  const submit = (e, parentOf) => {
    e.preventDefault();
    const title = text.trim();
    if (!title) return;
    setText('');
    setAdding(null);
    fold(parentOf.id, false);
    on.addSub(parentOf, title);
  };

  /** "+ Add sub-task" under `of`, or the box once pressed. */
  const adder = (of) => (adding === of.id ? (
    <form className="tdb-pop-add-form" onSubmit={(e) => submit(e, of)}
          onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); setAdding(null); setText(''); } }}>
      <input type="text" value={text} autoFocus maxLength={300} placeholder="Sub-task…"
             aria-label={`New sub-task of "${of.title}"`} onChange={(e) => setText(e.target.value)} />
      <button type="submit" className="btn btn-primary btn-small" disabled={busy || !text.trim()}>Add</button>
      <button type="button" className="btn btn-secondary btn-small"
              onClick={() => { setAdding(null); setText(''); }}>Cancel</button>
    </form>
  ) : (
    <button type="button" className="tda-add-line tdb-pop-add" disabled={busy}
            aria-label={`Add sub-task to "${of.title}"`}
            onClick={() => { setAdding(of.id); setText(''); }}>+ Add sub-task</button>
  ));

  /** The sub-tasks of `of`, at `lvl` (1 or 2 under the card), open then done. */
  const tree = (of, lvl) => {
    const open = kidsOf(of.id);
    const done = doneKidsOf(of.id);
    if (!open.length && !done.length) return null;
    return (
      <ul className={`tdb-pop-tree lvl-${lvl}`} aria-label={`Sub-tasks of "${of.title}"`}>
        {[...open.map((t) => [t, false]), ...done.map((t) => [t, true])].map(([t, isDone]) => {
          const n = isDone ? { open: 0, done: 0, total: 0 } : countUnder(t.id);
          const hasKids = n.total > 0 && level + lvl < MAX_LEVEL;
          const isFolded = hasKids && folded.has(t.id);
          const tp = Number(t.priority) || 4;
          return (
            <li key={t.id} className={`tdb-pop-row${isDone ? ' is-done' : ''}`} data-pop-row={t.id}>
              <div className="tdb-pop-line">
                {hasKids ? (
                  <button type="button" className={`ctd-twist${isFolded ? ' is-folded' : ''}`}
                          aria-expanded={!isFolded} aria-label={`${isFolded ? 'Show' : 'Hide'} sub-tasks of "${t.title}"`}
                          onClick={() => fold(t.id, !isFolded)}><Icon name="chevronDown" size={14} /></button>
                ) : <span className="ctd-twist-gap" aria-hidden="true" />}
                <label className={`ctd-check p${isDone ? 4 : tp}`} title={isDone ? 'Not done after all' : 'Done'}>
                  <input type="checkbox" checked={isDone || ticking(t)} disabled={busy}
                         onChange={() => (isDone ? on.reopen(t) : on.complete(t))}
                         aria-label={isDone ? `Mark "${t.title}" as not done` : `Mark "${t.title}" as done`} />
                  <span className="ctd-ring" aria-hidden="true" />
                </label>
                {on.open && !isDone ? (
                  <button type="button" className="tdb-pop-sub is-link" onClick={() => on.open(t)}
                          title="Open this sub-task">{t.title}</button>
                ) : <span className="tdb-pop-sub">{t.title}</span>}
                {!isDone && t.due_date && (
                  <span className={`ctd-due is-${dueState(t, todayIso)} is-static`}>
                    <Icon name="calendar" size={12} />{dueLabel(String(t.due_date).slice(0, 10), t.due_time, now)}
                  </span>
                )}
                {n.total > 0 && (
                  <span className="ctd-count" title={`${n.done} of ${n.total} sub-tasks done`}>{n.done}/{n.total}</span>
                )}
              </div>
              {hasKids && !isFolded && tree(t, lvl + 1)}
              {!isDone && !isFolded && level + lvl < MAX_LEVEL && (
                <div className="tdb-pop-under">{adder(t)}</div>
              )}
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <section className="tdb-pop-subs" ref={subsRef} aria-label="Sub-tasks" data-subtask-section="">
      <h4 className="tdb-pop-h">
        Sub-tasks
        {totals.total > 0 && (
          <span className="ctd-count" data-subtasks={`${totals.done}/${totals.total}`}
                title={`${totals.done} of ${totals.total} sub-tasks done`}>{totals.done}/{totals.total}</span>
        )}
      </h4>
      {tree(item, 1) || (level < MAX_LEVEL && <p className="tdb-pop-none">No sub-tasks yet.</p>)}
      {level < MAX_LEVEL && adder(item)}
    </section>
  );
}
